import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BRAND = "#069d6e";

const apiBase = () => {
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

const statusColor = (status) => {
  switch (status) {
    case "delivered":
      return { bg: "#e6f7f0", color: "#069d6e" };
    case "bounced":
    case "dropped":
      return { bg: "#fdecea", color: "#d32f2f" };
    case "spam":
      return { bg: "#fff4e5", color: "#ed6c02" };
    default:
      return { bg: "#eef1f4", color: "#555" };
  }
};

function StatusChip({ label, status }) {
  const c = statusColor(status);
  return (
    <Chip
      label={label}
      size="small"
      sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600 }}
    />
  );
}

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loader, setLoader] = useState(true);
  const [selected, setSelected] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [toast, setToast] = useState(null);

  const sendCampaign = async (campaign) => {
    if (sendingId) return;
    const ok = window.confirm(
      `Send "${campaign.subject}" to its recipients now?`
    );
    if (!ok) return;
    setSendingId(campaign.id);
    try {
      const { data } = await axios.post(
        `${apiBase()}/email/campaigns/${campaign.id}/send`
      );
      setToast({
        type: data?.success ? "success" : "error",
        msg: data?.success
          ? `Sent to ${data?.totalSent ?? "the"} recipients.`
          : data?.message || "Send failed.",
      });
      await getCampaigns();
    } catch (error) {
      console.error("Send campaign error:", error?.response?.data || error);
      setToast({
        type: "error",
        msg: error?.response?.data?.message || "Failed to send campaign.",
      });
    } finally {
      setSendingId(null);
    }
  };

  const getCampaigns = async () => {
    setLoader(true);
    try {
      const { data } = await axios.get(`${apiBase()}/email/campaigns`);
      setCampaigns(data?.campaigns || []);
    } catch (error) {
      console.error("Failed to load campaigns:", error);
      setCampaigns([]);
    }
    setLoader(false);
  };

  useEffect(() => {
    getCampaigns();
  }, []);

  const openCampaign = async (campaign) => {
    setSelected(campaign);
    setRecipients([]);
    setSearch("");
    setRecipientsLoading(true);
    try {
      const { data } = await axios.get(
        `${apiBase()}/email/campaigns/${campaign.id}/recipients`
      );
      setRecipients(data?.recipients || []);
    } catch (error) {
      console.error("Failed to load recipients:", error);
      setRecipients([]);
    }
    setRecipientsLoading(false);
  };

  const closeDialog = () => {
    setSelected(null);
    setRecipients([]);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const filteredRecipients = recipients.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.email?.toLowerCase().includes(q) ||
      r.name?.toLowerCase().includes(q) ||
      r.source?.toLowerCase().includes(q)
    );
  });

  const deliveredCount = recipients.filter((r) => r.status === "delivered").length;
  const openedCount = recipients.filter((r) => r.opened).length;
  const bouncedCount = recipients.filter(
    (r) => r.status === "bounced" || r.status === "dropped"
  ).length;

  return (
    <div className="min-h-screen">
      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert
            severity={toast.type}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <div className="p-10 max-md:p-5 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <IconButton onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <div className="text-black font-semibold text-lg">Campaigns</div>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate("/create-campaign")}
            sx={{
              backgroundColor: BRAND,
              color: "white",
              "&:hover": { backgroundColor: BRAND, color: "white" },
            }}
          >
            Create Campaign
          </Button>
          <Button
            onClick={getCampaigns}
            variant="outlined"
            sx={{ borderColor: BRAND, color: BRAND, "&:hover": { borderColor: BRAND } }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="px-10 max-md:px-5 pb-10">
        {loader ? (
          <div className="flex justify-center mt-10">
            <CircularProgress style={{ color: BRAND }} />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex justify-center text-black mt-10">
            No campaigns yet. Click "Create Campaign" to add one.
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7f9" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipients</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Audience</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TablebodyRows
                campaigns={campaigns}
                openCampaign={openCampaign}
                formatDate={formatDate}
                sendCampaign={sendCampaign}
                sendingId={sendingId}
              />
            </Table>
          </TableContainer>
        )}
      </div>

      <Dialog open={!!selected} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          <div className="font-semibold">{selected?.subject}</div>
          <div className="text-sm text-gray-500 font-normal">
            Sent {formatDate(selected?.sent_at)} · {selected?.recipient_count}{" "}
            recipients
          </div>
          <IconButton
            onClick={closeDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="flex gap-2 flex-wrap mb-4">
            <StatusChip label={`${recipients.length} total`} status="default" />
            <StatusChip label={`${deliveredCount} delivered`} status="delivered" />
            <StatusChip label={`${openedCount} opened`} status="default" />
            {bouncedCount > 0 && (
              <StatusChip label={`${bouncedCount} bounced`} status="bounced" />
            )}
          </div>

          <TextField
            fullWidth
            size="small"
            placeholder="Search by email or source"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {recipientsLoading ? (
            <div className="flex justify-center my-6">
              <CircularProgress style={{ color: BRAND }} size={28} />
            </div>
          ) : filteredRecipients.length === 0 ? (
            <div className="text-center text-gray-500 my-6">
              No recipients found.
            </div>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f7f9" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Opened</TableCell>
                  </TableRow>
                </TableHead>
                <RecipientRows recipients={filteredRecipients} />
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TablebodyRows({
  campaigns,
  openCampaign,
  formatDate,
  sendCampaign,
  sendingId,
}) {
  return (
    <TableBody>
      {campaigns.map((c) => {
        const audience = [];
        if (c.included_waitlist) audience.push("Waitlist");
        if (Array.isArray(c.selected_groups) && c.selected_groups.length > 0)
          audience.push(...c.selected_groups);
        if (Array.isArray(c.selected_emails) && c.selected_emails.length > 0)
          audience.push(`${c.selected_emails.length} user(s)`);
        const customCount = Array.isArray(c.other_emails)
          ? c.other_emails.length
          : c.other_emails_count || 0;
        if (customCount > 0) audience.push(`${customCount} custom`);

        const isDraft = c.status === "draft";
        const busy = sendingId === c.id;
        return (
          <TableRow key={c.id} hover>
            <TableCell>{c.subject}</TableCell>
            <TableCell>
              <Chip
                label={isDraft ? "Draft" : "Sent"}
                size="small"
                sx={{
                  backgroundColor: isDraft ? "#eef1f4" : "#e6f7f0",
                  color: isDraft ? "#555" : BRAND,
                  fontWeight: 600,
                }}
              />
            </TableCell>
            <TableCell>{c.recipient_count || 0}</TableCell>
            <TableCell>
              {audience.length > 0 ? audience.join(", ") : "-"}
            </TableCell>
            <TableCell>{isDraft ? "-" : formatDate(c.last_sent_at || c.sent_at)}</TableCell>
            <TableCell align="right">
              <Button
                size="small"
                disabled={busy}
                onClick={() => sendCampaign(c)}
                variant="contained"
                sx={{
                  backgroundColor: BRAND,
                  color: "#fff",
                  textTransform: "none",
                  mr: 1,
                  "&:hover": { backgroundColor: BRAND },
                  "&.Mui-disabled": { backgroundColor: "#9ccabb", color: "#fff" },
                }}
              >
                {busy ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : isDraft ? (
                  "Send Email"
                ) : (
                  "Resend"
                )}
              </Button>
              <Button
                size="small"
                onClick={() => openCampaign(c)}
                sx={{ color: BRAND, textTransform: "none" }}
              >
                View recipients
              </Button>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
}

function RecipientRows({ recipients }) {
  return (
    <TableBody>
      {recipients.map((r) => (
        <TableRow key={r.id} hover>
          <TableCell>{r.email}</TableCell>
          <TableCell>{r.source || "-"}</TableCell>
          <TableCell>
            <StatusChip label={r.status || "sent"} status={r.status} />
          </TableCell>
          <TableCell>{r.opened ? "Yes" : "No"}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}
